import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type GrafanaEmbedFrameProps = {
  url: string;
  title: string;
  onRetry: () => void;
};

export function GrafanaEmbedFrame({ url, title, onRetry }: GrafanaEmbedFrameProps): JSX.Element {
  const grafanaOrigin = new URL(url).origin;
  const [probeState, setProbeState] = useState<"checking" | "allowed" | "blocked">("checking");
  const [accessHelpOpen, setAccessHelpOpen] = useState<boolean>(false);
  const [probeAttempt, setProbeAttempt] = useState(0);
  const probeFrameRef = useRef<HTMLIFrameElement>(null);
  const interactiveProbeFrameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    setProbeState("checking");
    setAccessHelpOpen(false);
    const timeout = window.setTimeout(() => setProbeState("blocked"), 8_000);
    const onMessage = (event: MessageEvent<unknown>): void => {
      const isProbeFrame =
        event.source === probeFrameRef.current?.contentWindow ||
        event.source === interactiveProbeFrameRef.current?.contentWindow;
      if (event.origin !== grafanaOrigin || !isProbeFrame) {
        return;
      }
      if (!isCookieProbeMessage(event.data)) return;
      window.clearTimeout(timeout);
      setProbeState(event.data.allowed ? "allowed" : "blocked");
      if (event.data.allowed) {
        setAccessHelpOpen(false);
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timeout);
    };
  }, [grafanaOrigin, probeAttempt]);

  const retryProbe = (): void => {
    setAccessHelpOpen(false);
    setProbeState("checking");
    setProbeAttempt((attempt) => attempt + 1);
    onRetry();
  };

  return (
    <>
      <iframe
        key={probeAttempt}
        ref={probeFrameRef}
        className="absolute h-px w-px opacity-0"
        src={`${grafanaOrigin}/esumei/embed-cookie-probe`}
        title="Grafana cookie compatibility check"
        allow="storage-access"
        aria-hidden="true"
      />
      <iframe
        className="h-[calc(100vh-12rem)] min-h-[720px] w-full rounded-lg border border-border bg-card"
        src={url}
        title={title}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <Dialog open={accessHelpOpen} onOpenChange={setAccessHelpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Embedded dashboard could not be authorized</DialogTitle>
            <DialogDescription>
              Your browser is blocking the Grafana session cookie in this embedded dashboard. Select
              Enable dashboard access below. Your browser may ask for permission. If it still fails,
              allow third-party cookies for mqtt.metering.app, then select Retry.
            </DialogDescription>
          </DialogHeader>
          <iframe
            ref={interactiveProbeFrameRef}
            className="h-24 w-full rounded-md border border-border bg-card"
            src={`${grafanaOrigin}/esumei/embed-cookie-probe`}
            title="Enable Grafana dashboard access"
            allow="storage-access"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessHelpOpen(false)}>
              Close
            </Button>
            <Button onClick={retryProbe}>Retry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {probeState === "blocked" ? (
        <div className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs text-muted-foreground">
          <span>
            The embedded dashboard could not confirm browser cookie access. Update the browser
            privacy setting, then retry.
          </span>
          <div className="flex shrink-0 gap-1">
            <Button variant="ghost" size="sm" onClick={() => setAccessHelpOpen(true)}>
              Enable access
            </Button>
            <Button variant="ghost" size="sm" onClick={retryProbe}>
              Retry
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function isCookieProbeMessage(
  data: unknown,
): data is { type: "esumei-grafana-cookie-probe"; allowed: boolean } {
  if (typeof data !== "object" || data === null) return false;
  const message = data as { type?: unknown; allowed?: unknown };
  return message.type === "esumei-grafana-cookie-probe" && typeof message.allowed === "boolean";
}
